import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useDiscordOAuth, useLinkDiscord } from '@/hooks/queries'
import { useAuthStore } from '@/store/auth'
import { useOverlayStore } from '@/store/overlays'
import { notifyError } from '@/lib/notify'
import { takeReturnPath } from '@/lib/return-path'
import { loadDraftGiveaway, takeWizardResume } from '@/lib/draft-storage'
import { CONNECT_TAB_STATE } from '@/lib/oauth'

/**
 * Port of pages/AuthDiscord.vue — the Discord OAuth callback.
 *
 * Two fixes over the original: React 18/19 double-invokes effects in dev, so
 * the exchange is guarded against running twice with the same single-use code;
 * and on success it navigates in-app instead of doing
 * `window.location.href = origin + "/"`, which threw away the SPA and any state.
 *
 * Signed out, the code signs in (or registers). Signed in, it links Discord to
 * the current account instead — /auth/discord would otherwise switch to, or
 * create, whichever account owns that Discord identity.
 *
 * Linking started from a page on the web runs in a tab of its own (state =
 * CONNECT_TAB_STATE). That tab just reports the result and tries to close: the
 * page that opened it is still there and picks the new connection up over the
 * account events socket.
 */
export function AuthDiscord() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const signIn = useDiscordOAuth()
  const link = useLinkDiscord()
  const overlays = useOverlayStore()
  const started = useRef(false)

  // Read once: signing in flips this, and the branch must not change mid-flight.
  const [linking] = useState(() => useAuthStore.getState().isAuthenticated)
  const code = searchParams.get('code')
  const ownTab = linking && searchParams.get('state') === CONNECT_TAB_STATE
  const [tabResult, setTabResult] = useState<'done' | 'failed' | null>(null)

  useEffect(() => {
    if (started.current) return
    started.current = true

    const backToAuth = () => {
      overlays.open('auth')
      navigate('/', { replace: true })
    }

    // Return to where the user started, reopening the giveaway wizard when the
    // connect button was pressed inside it. Runs whether or not linking worked.
    const backToWhereTheyWere = () => {
      const resumeWizard = takeWizardResume()
      navigate(takeReturnPath(), { replace: true })
      if (resumeWizard) {
        const draft = loadDraftGiveaway()
        if (draft) overlays.setDraftGiveaway(draft)
        overlays.open('new-giveaway')
      }
    }

    if (ownTab) {
      if (!code) {
        notifyError('Unable to authorize your Discord account')
        setTabResult('failed')
        return
      }
      // Failures are reported by the global mutation error toast.
      link.mutateAsync(code).then(
        () => {
          setTabResult('done')
          // Allowed because the app opened this tab; if the browser refuses,
          // the message below says what to do.
          window.setTimeout(() => window.close(), 1500)
        },
        () => setTabResult('failed'),
      )
      return
    }

    if (!code) {
      notifyError('Unable to authorize your Discord account')
      if (linking) backToWhereTheyWere()
      else backToAuth()
      return
    }

    if (linking) {
      // Failures are reported by the global mutation error toast.
      link
        .mutateAsync(code)
        .catch(() => undefined)
        .finally(backToWhereTheyWere)
      return
    }

    signIn
      .mutateAsync(code)
      .then(() => navigate(takeReturnPath(), { replace: true }))
      .catch(backToAuth)
    // The mutations are stable objects; the guard above is what prevents reruns.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code])

  if (ownTab && tabResult) {
    return (
      <div className="empty-state" role="status">
        <div className="empty-state-title">
          {tabResult === 'done' ? 'Discord connected' : 'Discord was not connected'}
        </div>
        <p className="empty-state-body">
          {tabResult === 'done'
            ? 'You can close this tab and carry on where you were.'
            : 'Close this tab and press Connect again to retry.'}
        </p>
        <Link to="/" replace className="link-button">
          Go to JustLoot.me
        </Link>
      </div>
    )
  }

  return (
    <div className="content-row medium upper" style={{ padding: 48 }}>
      {linking ? 'Connecting your Discord account…' : 'Signing you in…'}
    </div>
  )
}
